import Cookies from 'js-cookie';
import { flattenQuestionsForApi } from '../components/CreateNewTask/utils/questionStructure.js';

const deleteCookieByKey = (key) => {
  if (typeof document === 'undefined') {
    return;
  }
  document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
};

export const setLocalStorage = (key, value) => {
  // Never persist undefined/null: JSON.stringify(undefined) === undefined, which
  // localStorage.setItem coerces to the literal string "undefined" — and the next
  // getLocalStorage then throws `"undefined" is not valid JSON` on JSON.parse,
  // crashing the auth/render path (e.g. an exchange response missing a field).
  if (value === undefined || value === null) {
    localStorage.removeItem(key);
    deleteCookieByKey(key);
    return;
  }
  localStorage.setItem(key, JSON.stringify(value));
  deleteCookieByKey(key);
};

export const getLocalStorage = (key) => {
  const localStorageValue = localStorage.getItem(key);
  // Tolerate legacy "undefined"/"null" strings and any non-JSON value instead of
  // throwing — a single malformed key must not crash the whole app.
  if (
    localStorageValue &&
    localStorageValue !== 'undefined' &&
    localStorageValue !== 'null'
  ) {
    try {
      return JSON.parse(localStorageValue);
    } catch (e) {
      return localStorageValue;
    }
  }
  return Cookies.get(key);
};

export const deleteLocalStorage = (key) => {
  localStorage.removeItem(key);
};

/**
 * Resolve user states from profile object.
 * Returns non-empty array of state codes, or null if no states are set.
 */
export const resolveProfileStates = (profile) => {
  if (Array.isArray(profile?.states) && profile.states.filter(Boolean).length > 0) {
    return profile.states.filter(Boolean);
  }
  if (typeof profile?.state === 'string' && profile.state.trim()) {
    return [profile.state.trim()];
  }
  return null;
};

export const getUserStates = () => {
  const stored = getLocalStorage('states');
  if (Array.isArray(stored) && stored.filter(Boolean).length > 0) return stored.filter(Boolean);
  const single = getLocalStorage('state');
  if (typeof single === 'string' && single.trim()) return [single.trim()];
  return [];
};

const cloneAdvancedMarkingSettingsByClassId = (settingsByClassId, archivedIds) => {
  if (
    !settingsByClassId ||
    typeof settingsByClassId !== 'object' ||
    Array.isArray(settingsByClassId)
  ) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(settingsByClassId)
      .filter(([classId]) => !archivedIds.has(String(classId)))
      .map(([classId, controls]) => [
        classId,
        controls && typeof controls === 'object' ? { ...controls } : controls,
      ]),
  );
};

// JED-1289: a duplicate leaves archived classes behind. The class picker hides
// them, so a copy that kept one went to that class's students as well as to the
// class the teacher picked. Only classes the teacher's class list marks archived
// are dropped; an id missing from the list is kept, as before.
const archivedClassIdsIn = (classes) =>
  new Set(
    (Array.isArray(classes) ? classes : [])
      .filter((clazz) => clazz && clazz.isArchived)
      .map((clazz) => String(clazz.id)),
  );

export const buildDuplicateAssignmentPayload = (assignment, classes) => {
  if (!assignment) {
    throw new Error('Task not found for duplication');
  }

  if (
    !Array.isArray(assignment.questions) ||
    !Array.isArray(assignment.classIds)
  ) {
    throw new Error('Invalid task data for duplication');
  }

  const archivedIds = archivedClassIdsIn(classes);

  return {
    title: `Copy of ${assignment.title || 'Untitled Task'}`,
    questions: flattenQuestionsForApi(assignment.questions || []),
    classIds: assignment.classIds.filter((id) => !archivedIds.has(String(id))),
    dueAt: assignment.dueAt,
    reviewedBy: assignment.reviewedBy,
    reviewers: assignment.reviewers || {},
    subject: assignment.subject || '',
    subjects: assignment.subjects || [],
    years: assignment.years || [],
    taskType: assignment.taskType,
    advancedMarkingSettingsByClassId: cloneAdvancedMarkingSettingsByClassId(
      assignment.advancedMarkingSettingsByClassId,
      archivedIds,
    ),
  };
};

// `classes` is the teacher's full class list (useClassData), archived ones included.
export const duplicateAssignment = async (id, assignments, classes) => {
  const assignment = assignments.find(
    (assignment) => String(assignment.id) === String(id),
  );

  const copyAssignment = buildDuplicateAssignmentPayload(assignment, classes);
  const { createAssignment } = await import('../service');
  const createdAssignment = await createAssignment(copyAssignment);
  if (!createdAssignment) {
    throw new Error('Failed to duplicate task');
  }
  return createdAssignment;
};
